import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Building2, Users, UserPlus, DollarSign, TrendingUp, Percent,
  Phone, Mail, MapPin, Edit, Trash2, Plus, Search, Eye, ChevronRight,
  RefreshCw, CheckCircle, Clock, Building, Home, Receipt,
  AlertTriangle, IdCard,
} from 'lucide-react';
import { useDedupGuard } from '@/components/BrokerDedupModals';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrokerMaster = () => {
  const { token, user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  const isAdmin = ['admin', 'owner', 'ceo', 'hod', 'super_admin'].some((h) => role.includes(h));
  // Three dedup guards — one per create flow. Each owns its own modal
  // state and renders its own `<guard.Modal />` exactly once below.
  const brokerGuard = useDedupGuard({ kind: 'broker', token, isAdmin });
  const agentGuard = useDedupGuard({ kind: 'agent', token, isAdmin });
  const leadGuard = useDedupGuard({ kind: 'lead', token, isAdmin });

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('brokers');
  
  // Broker state
  const [brokers, setBrokers] = useState([]);
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [showBrokerDialog, setShowBrokerDialog] = useState(false);
  const [editingBroker, setEditingBroker] = useState(null);
  const [brokerSearch, setBrokerSearch] = useState('');
  
  // Agent state
  const [agents, setAgents] = useState([]);
  const [showAgentDialog, setShowAgentDialog] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  
  // Sales state
  const [sales, setSales] = useState([]);
  const [showSaleDialog, setShowSaleDialog] = useState(false);
  const [allAgents, setAllAgents] = useState([]);
  const [allDevelopers, setAllDevelopers] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  
  // Commission summary
  const [summary, setSummary] = useState(null);
  const [topPerformers, setTopPerformers] = useState({ top_brokers: [], top_agents: [] });
  
  // Form state for broker. RERA / trade license added for the dedup
  // feature — they're optional but strongly preferred because they
  // short-circuit fuzzy matching to a 100% confidence merge candidate.
  const [brokerForm, setBrokerForm] = useState({
    name: '', company: '', email: '', phone: '',
    rera_number: '', trade_license: '',
    commission_rate: 2.0, broker_share: 30, agent_share: 70,
    address: '', notes: ''
  });

  // Agent form gets a RERA card field — the canonical agent identifier.
  const [agentForm, setAgentForm] = useState({
    name: '', email: '', phone: '', rera_card: '', commission_override: '',
    address: '', notes: ''
  });

  // Sale form gets the dedup buyer-identity fields so the cross-broker
  // conflict check can canonicalize them. buyer_phone replaces the
  // overloaded buyer_contact while still keeping it for legacy data.
  const [saleForm, setSaleForm] = useState({
    broker_id: '', agent_id: '', property_name: '', property_type: 'Apartment',
    developer_id: '', developer_name: '', project_id: '', unit_number: '', sale_amount: '',
    sale_date: new Date().toISOString().split('T')[0],
    buyer_name: '', buyer_contact: '', buyer_phone: '', buyer_email: '',
    buyer_emirates_id: '', buyer_nationality: '', notes: ''
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Progressive — each section paints as its endpoint returns. Was
  // particularly painful here with 7 sequential/parallel calls.
  const fetchData = () => {
    setLoading(true);
    const tasks = [
      axios.get(`${API}/brokers?token=${token}`)
        .then((r) => setBrokers(r.data.brokers || []))
        .catch((e) => console.error('[BrokerMaster] brokers failed:', e)),
      axios.get(`${API}/broker-commission/summary?token=${token}`)
        .then((r) => setSummary(r.data.summary))
        .catch((e) => console.error('[BrokerMaster] summary failed:', e)),
      axios.get(`${API}/broker-commission/top-performers?token=${token}`)
        .then((r) => setTopPerformers(r.data))
        .catch((e) => console.error('[BrokerMaster] top-performers failed:', e)),
      axios.get(`${API}/agents/all?token=${token}`)
        .then((r) => setAllAgents(r.data.agents || []))
        .catch((e) => console.error('[BrokerMaster] agents failed:', e)),
      axios.get(`${API}/developers?token=${token}`)
        .then((r) => setAllDevelopers(r.data.developers || []))
        .catch((e) => console.error('[BrokerMaster] developers failed:', e)),
      axios.get(`${API}/projects?token=${token}`)
        .then((r) => setAllProjects(r.data.projects || []))
        .catch((e) => console.error('[BrokerMaster] projects failed:', e)),
      axios.get(`${API}/broker-sales?token=${token}&limit=100`)
        .then((r) => setSales(r.data.sales || []))
        .catch((e) => console.error('[BrokerMaster] sales failed:', e)),
    ];
    let done = false;
    const off = () => { if (!done) { done = true; setLoading(false); } };
    const safety = setTimeout(off, 3000);
    Promise.race(tasks).then(off);
    Promise.allSettled(tasks).finally(() => clearTimeout(safety));
  };

  const fetchAgents = async (brokerId) => {
    try {
      const response = await axios.get(`${API}/brokers/${brokerId}/agents?token=${token}`);
      setAgents(response.data.agents || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  // Broker handlers. On CREATE we run the live duplicate-check through
  // the guard hook first; on EDIT we skip the check (the user is
  // intentionally modifying a known record). The dedup_override_id
  // gets stamped on the new broker doc so audits can trace the
  // override back to the manager alert.
  const handleSaveBroker = async () => {
    try {
      if (editingBroker) {
        await axios.put(`${API}/brokers/${editingBroker.id}?token=${token}`, brokerForm);
        toast.success('Broker updated successfully');
      } else {
        const decision = await brokerGuard.check({
          name: brokerForm.name,
          company: brokerForm.company,
          email: brokerForm.email,
          phone: brokerForm.phone,
          rera_number: brokerForm.rera_number,
          trade_license: brokerForm.trade_license,
        });
        if (decision.use_existing) {
          toast.info(`Opening existing broker "${decision.use_existing.name}" instead`);
          setShowBrokerDialog(false);
          resetBrokerForm();
          // Surface the existing record to the user — selecting it
          // shows their agents/sales below.
          setSelectedBroker(decision.use_existing);
          fetchAgents(decision.use_existing.id);
          return;
        }
        if (!decision.proceed) {
          // User cancelled — leave the dialog open so they can edit.
          return;
        }
        await axios.post(`${API}/brokers?token=${token}`, {
          ...brokerForm,
          dedup_override_id: decision.override_id,
          dedup_status: decision.override_id ? 'override_pending_review' : 'verified_unique',
        });
        toast.success('Broker created successfully');
      }
      setShowBrokerDialog(false);
      resetBrokerForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save broker');
    }
  };

  const handleDeleteBroker = async (brokerId) => {
    if (!window.confirm('Are you sure you want to delete this broker?')) return;
    try {
      await axios.delete(`${API}/brokers/${brokerId}?token=${token}`);
      toast.success('Broker deleted');
      fetchData();
      if (selectedBroker?.id === brokerId) {
        setSelectedBroker(null);
        setAgents([]);
      }
    } catch (error) {
      toast.error('Failed to delete broker');
    }
  };

  const resetBrokerForm = () => {
    setBrokerForm({
      name: '', company: '', email: '', phone: '',
      rera_number: '', trade_license: '',
      commission_rate: 2.0, broker_share: 30, agent_share: 70,
      address: '', notes: ''
    });
    setEditingBroker(null);
  };

  // Agent handlers. The dedup check looks across ALL brokers (RERA
  // card is the canonical key) — if a match is found, the user is
  // offered "Transfer" which updates the existing agent's broker_id
  // instead of creating a parallel record.
  const handleSaveAgent = async () => {
    if (!selectedBroker) return;
    try {
      const payload = { ...agentForm };
      if (!payload.commission_override) delete payload.commission_override;
      else payload.commission_override = parseFloat(payload.commission_override);

      if (editingAgent) {
        await axios.put(`${API}/agents/${editingAgent.id}?token=${token}`, payload);
        toast.success('Agent updated successfully');
      } else {
        const decision = await agentGuard.check({
          name: agentForm.name,
          email: agentForm.email,
          phone: agentForm.phone,
          rera_card: agentForm.rera_card,
        });
        if (decision.transfer_from) {
          // Move the existing agent over to this broker rather than
          // creating a parallel record. We use PUT /agents/{id} which
          // accepts a broker_id field — and we log the previous one
          // for the activity log.
          await axios.put(
            `${API}/agents/${decision.transfer_from.id}?token=${token}`,
            {
              broker_id: selectedBroker.id,
              previous_broker_id: decision.transfer_from.broker_id,
              transferred_at: new Date().toISOString(),
            },
          );
          toast.success(`${decision.transfer_from.name} transferred to ${selectedBroker.name || selectedBroker.company}`);
          setShowAgentDialog(false);
          resetAgentForm();
          fetchAgents(selectedBroker.id);
          fetchData();
          return;
        }
        if (!decision.proceed) {
          return;
        }
        await axios.post(
          `${API}/brokers/${selectedBroker.id}/agents?token=${token}`,
          {
            ...payload,
            dedup_override_id: decision.override_id,
            dedup_status: decision.override_id ? 'override_pending_review' : 'verified_unique',
          },
        );
        toast.success('Agent created successfully');
      }
      setShowAgentDialog(false);
      resetAgentForm();
      fetchAgents(selectedBroker.id);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save agent');
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm('Are you sure you want to delete this agent?')) return;
    try {
      await axios.delete(`${API}/agents/${agentId}?token=${token}`);
      toast.success('Agent deleted');
      if (selectedBroker) fetchAgents(selectedBroker.id);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete agent');
    }
  };

  const resetAgentForm = () => {
    setAgentForm({
      name: '', email: '', phone: '', rera_card: '',
      commission_override: '', address: '', notes: '',
    });
    setEditingAgent(null);
  };

  // Sale handlers. The dedup check is cross-broker (the whole point
  // is to catch a different broker registering the same client). The
  // user's options on a match are: flag conflict (creates a row in
  // conflict_log + parks the new sale) or cancel. Override is only
  // offered to admins, intentionally — most cases need a manager
  // decision.
  const handleSaveSale = async () => {
    try {
      const payload = {
        ...saleForm,
        sale_amount: parseFloat(saleForm.sale_amount),
        // Buyer phone is what we actually canonicalize; buyer_contact
        // remains as a free-form fallback for legacy data.
        buyer_phone: saleForm.buyer_phone || saleForm.buyer_contact,
      };

      const decision = await leadGuard.check({
        phone: payload.buyer_phone,
        email: payload.buyer_email,
        emirates_id: payload.buyer_emirates_id,
        buyer_name: payload.buyer_name,
      });
      if (decision.conflict_raised) {
        // Conflict was logged on the backend; the new sale doesn't
        // land until the manager resolves. Close the dialog and the
        // user can come back later if they want to view the queue.
        setShowSaleDialog(false);
        resetSaleForm();
        return;
      }
      if (!decision.proceed) {
        // User cancelled — leave dialog open.
        return;
      }
      if (decision.override_id) {
        payload.dedup_override_id = decision.override_id;
      }

      const response = await axios.post(`${API}/broker-sales?token=${token}`, payload);

      if (response.data.success) {
        toast.success('Sale recorded successfully');
        setShowSaleDialog(false);
        resetSaleForm();
        fetchData();
      } else {
        toast.error(response.data.error || 'Failed to record sale');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record sale');
    }
  };

  const handleUpdatePaymentStatus = async (saleId, status) => {
    try {
      await axios.put(`${API}/broker-sales/${saleId}/payment?token=${token}`, {
        payment_status: status,
        payment_date: status === 'paid' ? new Date().toISOString().split('T')[0] : null
      });
      toast.success('Payment status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update payment status');
    }
  };

  const resetSaleForm = () => {
    setSaleForm({
      broker_id: '', agent_id: '', property_name: '', property_type: 'Apartment',
      developer_id: '', developer_name: '', project_id: '', unit_number: '', sale_amount: '',
      sale_date: new Date().toISOString().split('T')[0],
      buyer_name: '', buyer_contact: '', notes: ''
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const filteredBrokers = brokers.filter(b => 
    b.name?.toLowerCase().includes(brokerSearch.toLowerCase()) ||
    b.company?.toLowerCase().includes(brokerSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="broker-master-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-primary">Broker Master</h1>
          <p className="text-slate-500">Manage real estate brokers, agents, and commissions</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { resetSaleForm(); setShowSaleDialog(true); }} className="bg-green-600 hover:bg-green-700">
            <Receipt className="mr-2" size={16} />
            Record Sale
          </Button>
          <Button onClick={() => { resetBrokerForm(); setShowBrokerDialog(true); }} className="bg-accent hover:bg-accent/90">
            <Plus className="mr-2" size={16} />
            Add Broker
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Sales</p>
                <p className="text-2xl font-bold">{summary?.total_sales || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Building2 className="text-blue-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Sale Value</p>
                <p className="text-2xl font-bold">{formatCurrency(summary?.total_sale_amount)}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <TrendingUp className="text-green-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Commission</p>
                <p className="text-2xl font-bold">{formatCurrency(summary?.total_commission)}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <DollarSign className="text-purple-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending Payout</p>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(summary?.pending_commission)}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="text-amber-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="brokers">Brokers & Agents</TabsTrigger>
          <TabsTrigger value="sales">Sales & Commission</TabsTrigger>
          <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
        </TabsList>

        {/* Brokers & Agents Tab */}
        <TabsContent value="brokers" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Brokers List */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Brokers ({brokers.length})</CardTitle>
                </div>
                <div className="relative mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <Input
                    placeholder="Search brokers..."
                    value={brokerSearch}
                    onChange={(e) => setBrokerSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </CardHeader>
              <CardContent className="max-h-[500px] overflow-y-auto">
                {filteredBrokers.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Building2 className="mx-auto mb-2 opacity-50" size={32} />
                    <p>No brokers found</p>
                    <Button variant="link" onClick={() => setShowBrokerDialog(true)}>
                      Add your first broker
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredBrokers.map((broker) => (
                      <div
                        key={broker.id}
                        className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                          selectedBroker?.id === broker.id 
                            ? 'border-accent bg-accent/5' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        onClick={() => {
                          setSelectedBroker(broker);
                          fetchAgents(broker.id);
                        }}
                        data-testid={`broker-card-${broker.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium">{broker.name}</h3>
                              <Badge variant="outline" className="text-xs">
                                {broker.commission_rate}%
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-500">{broker.company}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Users size={12} /> {broker.total_agents} agents
                              </span>
                              <span className="flex items-center gap-1">
                                <Building2 size={12} /> {broker.total_sales} sales
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingBroker(broker);
                                setBrokerForm(broker);
                                setShowBrokerDialog(true);
                              }}
                            >
                              <Edit size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 hover:text-red-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteBroker(broker.id);
                              }}
                            >
                              <Trash2 size={14} />
                            </Button>
                            <ChevronRight size={16} className="text-slate-400" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Agents List */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">
                    {selectedBroker ? `Agents - ${selectedBroker.name}` : 'Select a Broker'}
                  </CardTitle>
                  {selectedBroker && (
                    <Button
                      size="sm"
                      onClick={() => { resetAgentForm(); setShowAgentDialog(true); }}
                    >
                      <UserPlus size={14} className="mr-1" /> Add Agent
                    </Button>
                  )}
                </div>
                {selectedBroker && (
                  <div className="flex gap-4 mt-2 text-sm">
                    <span className="text-slate-500">
                      Commission: <span className="font-medium text-primary">{selectedBroker.commission_rate}%</span>
                    </span>
                    <span className="text-slate-500">
                      Split: <span className="font-medium text-primary">{selectedBroker.agent_share}% Agent / {selectedBroker.broker_share}% Broker</span>
                    </span>
                  </div>
                )}
              </CardHeader>
              <CardContent className="max-h-[500px] overflow-y-auto">
                {!selectedBroker ? (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="mx-auto mb-2 opacity-50" size={32} />
                    <p>Select a broker to view agents</p>
                  </div>
                ) : agents.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="mx-auto mb-2 opacity-50" size={32} />
                    <p>No agents found</p>
                    <Button variant="link" onClick={() => setShowAgentDialog(true)}>
                      Add first agent
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {agents.map((agent) => (
                      <div
                        key={agent.id}
                        className="p-4 rounded-lg border border-slate-200 hover:border-slate-300"
                        data-testid={`agent-card-${agent.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-medium">{agent.name}</h3>
                            <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                              {agent.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone size={12} /> {agent.phone}
                                </span>
                              )}
                              {agent.email && (
                                <span className="flex items-center gap-1">
                                  <Mail size={12} /> {agent.email}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-xs">
                              <span className="text-slate-500">
                                Sales: <span className="font-medium">{agent.total_sales}</span>
                              </span>
                              <span className="text-green-600">
                                Commission: {formatCurrency(agent.total_commission)}
                              </span>
                              {agent.commission_override && (
                                <Badge variant="outline" className="text-xs">
                                  Custom: {agent.commission_override}%
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => {
                                setEditingAgent(agent);
                                setAgentForm({
                                  ...agent,
                                  commission_override: agent.commission_override || ''
                                });
                                setShowAgentDialog(true);
                              }}
                            >
                              <Edit size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 hover:text-red-700"
                              onClick={() => handleDeleteAgent(agent.id)}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Sales & Commission Tab */}
        <TabsContent value="sales" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Sales</CardTitle>
              <CardDescription>Track property sales and commission payouts</CardDescription>
            </CardHeader>
            <CardContent>
              {sales.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Receipt className="mx-auto mb-2 opacity-50" size={32} />
                  <p>No sales recorded yet</p>
                  <Button variant="link" onClick={() => setShowSaleDialog(true)}>
                    Record your first sale
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left text-sm text-slate-500">
                        <th className="pb-3 font-medium">Property</th>
                        <th className="pb-3 font-medium">Developer</th>
                        <th className="pb-3 font-medium">Agent</th>
                        <th className="pb-3 font-medium">Sale Amount</th>
                        <th className="pb-3 font-medium">Commission</th>
                        <th className="pb-3 font-medium">Status</th>
                        <th className="pb-3 font-medium">Date</th>
                        <th className="pb-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sales.map((sale) => (
                        <tr key={sale.id} className="border-b last:border-0">
                          <td className="py-3">
                            <div>
                              <p className="font-medium">{sale.property_name}</p>
                              <p className="text-xs text-slate-500">{sale.unit_number}</p>
                            </div>
                          </td>
                          <td className="py-3 text-sm">{sale.developer_name}</td>
                          <td className="py-3">
                            <div>
                              <p className="text-sm">{sale.agent_name}</p>
                              <p className="text-xs text-slate-500">{sale.broker_name}</p>
                            </div>
                          </td>
                          <td className="py-3 font-medium">{formatCurrency(sale.sale_amount)}</td>
                          <td className="py-3">
                            <div>
                              <p className="font-medium text-green-600">{formatCurrency(sale.total_commission)}</p>
                              <p className="text-xs text-slate-500">
                                Agent: {formatCurrency(sale.agent_commission)} | Broker: {formatCurrency(sale.broker_commission)}
                              </p>
                            </div>
                          </td>
                          <td className="py-3">
                            <Badge
                              variant={sale.payment_status === 'paid' ? 'default' : 'secondary'}
                              className={sale.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}
                            >
                              {sale.payment_status === 'paid' ? (
                                <><CheckCircle size={12} className="mr-1" /> Paid</>
                              ) : (
                                <><Clock size={12} className="mr-1" /> Pending</>
                              )}
                            </Badge>
                          </td>
                          <td className="py-3 text-sm text-slate-500">
                            {new Date(sale.sale_date).toLocaleDateString('en-US', { timeZone: 'Asia/Dubai', month: 'short', day: 'numeric', year: 'numeric' })}
                          </td>
                          <td className="py-3">
                            {sale.payment_status === 'pending' ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUpdatePaymentStatus(sale.id, 'paid')}
                              >
                                Mark Paid
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleUpdatePaymentStatus(sale.id, 'pending')}
                              >
                                Revert
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Leaderboard Tab */}
        <TabsContent value="leaderboard" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 size={20} />
                  Top Brokers
                </CardTitle>
              </CardHeader>
              <CardContent>
                {topPerformers.top_brokers?.length === 0 ? (
                  <p className="text-center py-4 text-slate-500">No data yet</p>
                ) : (
                  <div className="space-y-3">
                    {topPerformers.top_brokers?.map((broker, index) => (
                      <div key={broker.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                            index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-slate-400' : 'bg-amber-700'
                          }`}>
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium">{broker.name}</p>
                            <p className="text-xs text-slate-500">{broker.company}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-600">{formatCurrency(broker.total_commission)}</p>
                          <p className="text-xs text-slate-500">{broker.total_sales} sales</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users size={20} />
                  Top Agents
                </CardTitle>
              </CardHeader>
              <CardContent>
                {topPerformers.top_agents?.length === 0 ? (
                  <p className="text-center py-4 text-slate-500">No data yet</p>
                ) : (
                  <div className="space-y-3">
                    {topPerformers.top_agents?.map((agent, index) => (
                      <div key={agent.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                            index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-slate-400' : 'bg-amber-700'
                          }`}>
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium">{agent.name}</p>
                            <p className="text-xs text-slate-500">{agent.broker_name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-600">{formatCurrency(agent.total_commission)}</p>
                          <p className="text-xs text-slate-500">{agent.total_sales} sales</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Broker Dialog */}
      <Dialog open={showBrokerDialog} onOpenChange={setShowBrokerDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingBroker ? 'Edit Broker' : 'Add New Broker'}</DialogTitle>
            <DialogDescription>
              {editingBroker ? 'Update broker details and commission settings' : 'Add a new real estate broker'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Broker Name *</Label>
                <Input
                  value={brokerForm.name}
                  onChange={(e) => setBrokerForm({...brokerForm, name: e.target.value})}
                  placeholder="John Smith"
                  data-testid="broker-name-input"
                />
              </div>
              <div>
                <Label>Company</Label>
                <Input
                  value={brokerForm.company}
                  onChange={(e) => setBrokerForm({...brokerForm, company: e.target.value})}
                  placeholder="ABC Realty"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={brokerForm.email}
                  onChange={(e) => setBrokerForm({...brokerForm, email: e.target.value})}
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={brokerForm.phone}
                  onChange={(e) => setBrokerForm({...brokerForm, phone: e.target.value})}
                  placeholder="+971 50 123 4567"
                />
              </div>
            </div>

            {/* RERA / Trade License — drives the dedup short-circuit.
                Optional, but a manager can run the workspace cleaner
                much more confidently when these are filled. */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-1.5">
                  <IdCard size={13} className="text-slate-400" />
                  RERA Number
                </Label>
                <Input
                  value={brokerForm.rera_number}
                  onChange={(e) => setBrokerForm({...brokerForm, rera_number: e.target.value})}
                  placeholder="e.g. 12345"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Used as the authoritative broker identifier — matches here override fuzzy name comparison.
                </p>
              </div>
              <div>
                <Label className="flex items-center gap-1.5">
                  <IdCard size={13} className="text-slate-400" />
                  Trade License
                </Label>
                <Input
                  value={brokerForm.trade_license}
                  onChange={(e) => setBrokerForm({...brokerForm, trade_license: e.target.value})}
                  placeholder="e.g. CN-1234567"
                />
              </div>
            </div>

            <Separator />

            <div>
              <Label className="text-base font-medium">Commission Settings</Label>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Commission Rate (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={brokerForm.commission_rate}
                  onChange={(e) => setBrokerForm({...brokerForm, commission_rate: parseFloat(e.target.value)})}
                  placeholder="2.0"
                />
                <p className="text-xs text-slate-500 mt-1">% of sale amount</p>
              </div>
              <div>
                <Label>Agent Share (%)</Label>
                <Input
                  type="number"
                  value={brokerForm.agent_share}
                  onChange={(e) => {
                    const agentShare = parseFloat(e.target.value);
                    setBrokerForm({
                      ...brokerForm, 
                      agent_share: agentShare,
                      broker_share: 100 - agentShare
                    });
                  }}
                  placeholder="70"
                />
              </div>
              <div>
                <Label>Broker Share (%)</Label>
                <Input
                  type="number"
                  value={brokerForm.broker_share}
                  disabled
                  className="bg-slate-50"
                />
              </div>
            </div>
            
            <div>
              <Label>Address</Label>
              <Input
                value={brokerForm.address}
                onChange={(e) => setBrokerForm({...brokerForm, address: e.target.value})}
                placeholder="Office address"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowBrokerDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveBroker} className="bg-accent hover:bg-accent/90" data-testid="save-broker-btn">
                {editingBroker ? 'Update' : 'Create'} Broker
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Agent Dialog */}
      <Dialog open={showAgentDialog} onOpenChange={setShowAgentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingAgent ? 'Edit Agent' : 'Add New Agent'}</DialogTitle>
            <DialogDescription>
              {selectedBroker && `Agent under ${selectedBroker.name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Agent Name *</Label>
              <Input
                value={agentForm.name}
                onChange={(e) => setAgentForm({...agentForm, name: e.target.value})}
                placeholder="Agent name"
                data-testid="agent-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={agentForm.email}
                  onChange={(e) => setAgentForm({...agentForm, email: e.target.value})}
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={agentForm.phone}
                  onChange={(e) => setAgentForm({...agentForm, phone: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label className="flex items-center gap-1.5">
                <IdCard size={13} className="text-slate-400" />
                RERA Card Number
              </Label>
              <Input
                value={agentForm.rera_card}
                onChange={(e) => setAgentForm({...agentForm, rera_card: e.target.value})}
                placeholder="e.g. 67890"
              />
              <p className="text-xs text-slate-500 mt-1">
                Canonical agent identifier — a RERA card match instantly flags this agent as already on file under another broker.
              </p>
            </div>
            <div>
              <Label>Commission Override (%)</Label>
              <Input
                type="number"
                value={agentForm.commission_override}
                onChange={(e) => setAgentForm({...agentForm, commission_override: e.target.value})}
                placeholder={`Leave empty to use broker's rate (${selectedBroker?.agent_share}%)`}
              />
              <p className="text-xs text-slate-500 mt-1">Override the broker's default agent share</p>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowAgentDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveAgent} className="bg-accent hover:bg-accent/90" data-testid="save-agent-btn">
                {editingAgent ? 'Update' : 'Create'} Agent
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Record Sale Dialog */}
      <Dialog open={showSaleDialog} onOpenChange={setShowSaleDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Record Property Sale</DialogTitle>
            <DialogDescription>Record a new sale and calculate commission automatically</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Select Broker *</Label>
                <Select
                  value={saleForm.broker_id}
                  onValueChange={(value) => {
                    setSaleForm({...saleForm, broker_id: value, agent_id: ''});
                  }}
                >
                  <SelectTrigger data-testid="sale-broker-select">
                    <SelectValue placeholder="Choose broker" />
                  </SelectTrigger>
                  <SelectContent>
                    {brokers.map((broker) => (
                      <SelectItem key={broker.id} value={broker.id}>
                        {broker.name} ({broker.commission_rate}%)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Select Agent *</Label>
                <Select
                  value={saleForm.agent_id}
                  onValueChange={(value) => setSaleForm({...saleForm, agent_id: value})}
                  disabled={!saleForm.broker_id}
                >
                  <SelectTrigger data-testid="sale-agent-select">
                    <SelectValue placeholder="Choose agent" />
                  </SelectTrigger>
                  <SelectContent>
                    {allAgents
                      .filter(a => a.broker_id === saleForm.broker_id)
                      .map((agent) => (
                        <SelectItem key={agent.id} value={agent.id}>
                          {agent.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <Separator />
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Property Name *</Label>
                <Input
                  value={saleForm.property_name}
                  onChange={(e) => setSaleForm({...saleForm, property_name: e.target.value})}
                  placeholder="e.g., Prestige Lakeside"
                  data-testid="sale-property-input"
                />
              </div>
              <div>
                <Label>Property Type</Label>
                <Select
                  value={saleForm.property_type}
                  onValueChange={(value) => setSaleForm({...saleForm, property_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Apartment">Apartment</SelectItem>
                    <SelectItem value="Villa">Villa</SelectItem>
                    <SelectItem value="Plot">Plot</SelectItem>
                    <SelectItem value="Commercial">Commercial</SelectItem>
                    <SelectItem value="Office">Office Space</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Developer Name *</Label>
                <Input
                  value={saleForm.developer_name}
                  onChange={(e) => setSaleForm({...saleForm, developer_name: e.target.value})}
                  placeholder="e.g., Prestige Group"
                />
              </div>
              <div>
                <Label>Unit Number</Label>
                <Input
                  value={saleForm.unit_number}
                  onChange={(e) => setSaleForm({...saleForm, unit_number: e.target.value})}
                  placeholder="e.g., A-1502"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sale Amount (AED) *</Label>
                <Input
                  type="number"
                  value={saleForm.sale_amount}
                  onChange={(e) => setSaleForm({...saleForm, sale_amount: e.target.value})}
                  placeholder="e.g., 15000000"
                  data-testid="sale-amount-input"
                />
              </div>
              <div>
                <Label>Sale Date *</Label>
                <Input
                  type="date"
                  value={saleForm.sale_date}
                  onChange={(e) => setSaleForm({...saleForm, sale_date: e.target.value})}
                />
              </div>
            </div>
            
            <Separator />
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Buyer Name</Label>
                <Input
                  value={saleForm.buyer_name}
                  onChange={(e) => setSaleForm({...saleForm, buyer_name: e.target.value})}
                  placeholder="Customer name"
                />
              </div>
              <div>
                <Label>Nationality (optional)</Label>
                <Input
                  value={saleForm.buyer_nationality}
                  onChange={(e) => setSaleForm({...saleForm, buyer_nationality: e.target.value})}
                  placeholder="e.g. UAE, Indian"
                />
              </div>
            </div>

            {/* Three buyer-identity fields drive the cross-broker
                duplicate check. Phone is the primary key (normalized
                country codes); Emirates ID is the strongest signal
                when the client provides it. */}
            <div className="rounded-md border border-amber-200 bg-amber-50/40 dark:bg-amber-950/10 p-3 space-y-2">
              <p className="text-xs flex items-center gap-1.5 text-amber-800 dark:text-amber-300">
                <AlertTriangle size={13} />
                Buyer ID fields — used for cross-broker duplicate detection
              </p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input
                    value={saleForm.buyer_phone}
                    onChange={(e) => setSaleForm({...saleForm, buyer_phone: e.target.value})}
                    placeholder="+971 50 123 4567"
                  />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input
                    type="email"
                    value={saleForm.buyer_email}
                    onChange={(e) => setSaleForm({...saleForm, buyer_email: e.target.value})}
                    placeholder="buyer@example.com"
                  />
                </div>
                <div>
                  <Label className="text-xs">Emirates ID</Label>
                  <Input
                    value={saleForm.buyer_emirates_id}
                    onChange={(e) => setSaleForm({...saleForm, buyer_emirates_id: e.target.value})}
                    placeholder="784-1990-1234567-8"
                  />
                </div>
              </div>
              {/* Keep the legacy free-form contact for backwards
                  compat — old data only has this field, and some
                  staff prefer typing here. */}
              <div>
                <Label className="text-xs text-slate-500">Other contact (free-form)</Label>
                <Input
                  value={saleForm.buyer_contact}
                  onChange={(e) => setSaleForm({...saleForm, buyer_contact: e.target.value})}
                  placeholder="optional"
                />
              </div>
            </div>
            
            {saleForm.broker_id && saleForm.sale_amount && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm font-medium text-green-800 mb-2">Commission Preview</p>
                {(() => {
                  const broker = brokers.find(b => b.id === saleForm.broker_id);
                  const agent = allAgents.find(a => a.id === saleForm.agent_id);
                  if (!broker) return null;
                  
                  const amount = parseFloat(saleForm.sale_amount) || 0;
                  const commission = (amount * broker.commission_rate) / 100;
                  const agentShare = agent?.commission_override || broker.agent_share;
                  const brokerShare = 100 - agentShare;
                  const agentCommission = (commission * agentShare) / 100;
                  const brokerCommission = (commission * brokerShare) / 100;
                  
                  return (
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-green-600">Total Commission ({broker.commission_rate}%)</p>
                        <p className="font-bold text-lg">{formatCurrency(commission)}</p>
                      </div>
                      <div>
                        <p className="text-green-600">Agent ({agentShare}%)</p>
                        <p className="font-bold">{formatCurrency(agentCommission)}</p>
                      </div>
                      <div>
                        <p className="text-green-600">Broker ({brokerShare}%)</p>
                        <p className="font-bold">{formatCurrency(brokerCommission)}</p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowSaleDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleSaveSale} 
                className="bg-green-600 hover:bg-green-700"
                disabled={!saleForm.broker_id || !saleForm.agent_id || !saleForm.property_name || !saleForm.sale_amount}
                data-testid="save-sale-btn"
              >
                <Receipt className="mr-2" size={16} />
                Record Sale
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dedup guard modals — each owns its own state via the hook
          above. Mounted once at the end of the page so the live-check
          decision Promise has somewhere to render. */}
      <brokerGuard.Modal />
      <agentGuard.Modal />
      <leadGuard.Modal />
    </div>
  );
};

export default BrokerMaster;
